import React, { FC, useEffect, useRef, useState } from 'react';

import { InitConfig } from '@solidgate/client-sdk-loader';
import Payment, { OrderShort } from '@solidgate/react-sdk';
import clsx from 'clsx';
import { useTranslation } from 'react-i18next';
import { Swiper, SwiperSlide } from 'swiper/react';

import Api from '~/Api';
import Loader from '~/components/atoms/Loader';
import PaymentError from '~/components/molecules/PaymentError';
import Guarantee from '~/components/organisms/SolidCheckout/components/PaymentForm/V2/Guarantee';
import useErrors from '~/hooks/useErrors';
import {
  AnalyticPaymentMethods,
  MerchantData,
  PAYMENT_METHODS,
  SOLID_PAYMENT_METHOD_MAPPER,
} from '~/types/payment';
import { ProductType } from '~/types/product';

import { defaultFormStyles, getDefaultFormParams } from './formConfig';
import PaymentButton from './PaymentButton';
import styles from './styles.module.scss';
import {
  handleAlreadyProcessed,
  handlePaypalPayment,
  renderPaypalButton,
} from '../../../paypalMethods';

type Props = {
  products: ProductType[];
  onPaymentSuccess?: (data: {
    productId: string;
    productName: string;
    order: OrderShort;
  }) => void | Promise<void>;
  onPaymentFail?: (data: {
    productId: string;
    productName: string;
    message: string;
    method: AnalyticPaymentMethods;
  }) => void;
  onPaymentSubmit?: (data: {
    productId: string;
    productName: string;
    method: AnalyticPaymentMethods;
  }) => void;
  onPaymentView?: (data: { productId: string; productName: string }) => void;
  onPaymentMethodChange?: (type: PAYMENT_METHODS) => void;
  onCreateCardIntentError?: (errorMessage: string) => void;
  onFormReady?: () => void | Promise<void>;
  paymentApi: {
    createPaymentIntentCard: (
      productId: string,
      geoCountry: string,
    ) => Promise<{
      merchant: string;
      payment_intent: string;
      signature: string;
    }>;
    createPaymentIntentPaypal?: (productId: string) => Promise<{
      order: Record<string, any>;
      order_metadata: any;
      pay_form: Record<string, any>;
    }>;
  };
  formParams?: InitConfig['formParams'];
  formStyles?: InitConfig['styles'];
  SummaryComponent?: FC<{ product: ProductType }>;
};

const PaymentFormV3: FC<Props> = ({
  products,
  onPaymentSuccess,
  onPaymentFail,
  onPaymentSubmit,
  onPaymentView,
  onPaymentMethodChange,
  onCreateCardIntentError,
  onFormReady,
  paymentApi,
  formStyles,
  formParams,
  SummaryComponent,
}) => {
  const { t } = useTranslation('general');

  const selectedProduct = products[0];
  const [activeCard, setActiveCard] = useState<PAYMENT_METHODS>(
    PAYMENT_METHODS.ONE_PAY,
  );
  const [isFormReady, setIsFormReady] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [paymentError, setPaymentError] = useState('');
  const [paypalScript, setPaypalScript] = useState<string | null>(null);
  const [merchantData, setMerchantData] = useState<MerchantData | null>(null);

  const paypalBtnRef = useRef<HTMLDivElement | null>(null);
  const appleContainerRef = useRef<HTMLDivElement | null>(null);
  const googleContainerRef = useRef<HTMLDivElement | null>(null);
  const { reportUserErrors } = useErrors();

  const handlePaymentMethodChange = (type: PAYMENT_METHODS): void => {
    if (onPaymentMethodChange) {
      onPaymentMethodChange(type);
    }

    setActiveCard(type);
  };

  const handleCloseError = (): void => {
    setPaymentError('');
  };

  const handlePaymentSuccess = async (order: OrderShort): Promise<void> => {
    if (onPaymentSuccess) {
      await onPaymentSuccess({
        productId: selectedProduct.id,
        productName: selectedProduct.name,
        order: order,
      });
    }

    setIsLoading(false);
  };

  const handlePaymentFail = (
    message: string,
    method: AnalyticPaymentMethods,
  ): void => {
    if (onPaymentFail) {
      onPaymentFail({
        productId: selectedProduct.id,
        productName: selectedProduct.name,
        message,
        method,
      });
    }

    setIsLoading(false);
    setMerchantData(null);
    setPaypalScript(null);
    setPaymentError(message);
  };

  const handlePaymentSubmit = (method: AnalyticPaymentMethods): void => {
    if (onPaymentSubmit) {
      onPaymentSubmit({
        productId: selectedProduct.id,
        productName: selectedProduct.name,
        method,
      });
    }

    setIsLoading(true);
  };

  useEffect(() => {
    if (!merchantData) {
      (async (): Promise<void> => {
        try {
          const country = await Api.getCountry();
          const data = await paymentApi.createPaymentIntentCard(
            selectedProduct.id,
            country,
          );
          setMerchantData({ ...data, paymentIntent: data.payment_intent });
        } catch (e: any) {
          const errorMessage = e.error || e.message || 'Error :(';
          const isPaymentError = e.message?.includes('createPaymentIntentCard');
          reportUserErrors({
            error: e,
            method: 'createPaymentIntentCard',
            userMessage: isPaymentError
              ? t(
                  'error:create_payment_intent_card',
                  'Card payment initiation failed. Please re-enter your details or',
                )
              : errorMessage,
          });

          onCreateCardIntentError && onCreateCardIntentError(errorMessage);
        }
      })();
    }
  }, [merchantData]);

  useEffect(() => {
    if (!paypalScript) {
      (async (): Promise<void> => {
        try {
          if (!paymentApi.createPaymentIntentPaypal) {
            return;
          }
          const paypalData = await paymentApi.createPaymentIntentPaypal(
            selectedProduct.id,
          );

          setPaypalScript(paypalData.pay_form.script_url);
          renderPaypalButton(paypalData.pay_form.script_url);
        } catch (e: any) {
          const isPaymentError = e.message.includes(
            'createPaymentIntentPaypal',
          );
          reportUserErrors({
            error: e,
            method: 'createPaymentIntentPaypal',
            userMessage: isPaymentError
              ? t(
                  'error:create_payment_intent_paypal',
                  'Paypal payment initiation unsuccessful. Try again or',
                )
              : e.error || e.message || 'Error :(',
          });
        }
      })();
    }
  }, [paypalScript]);

  useEffect(() => {
    if (!paymentApi.createPaymentIntentPaypal) {
      return;
    }

    paypalBtnRef.current?.addEventListener('order-started-processing', () =>
      handlePaymentSubmit(PAYMENT_METHODS.PAYPAL),
    );

    paypalBtnRef.current?.addEventListener(
      'order-processed',
      handlePaypalPayment,
    );

    paypalBtnRef.current?.addEventListener(
      'order-already-processed',
      handleAlreadyProcessed,
    );

    return (): void => {
      paypalBtnRef.current?.removeEventListener(
        'order-started-processing',
        () => handlePaymentSubmit(PAYMENT_METHODS.PAYPAL),
      );

      paypalBtnRef.current?.removeEventListener(
        'order-processed',
        handlePaypalPayment,
      );

      paypalBtnRef.current?.removeEventListener(
        'order-already-processed',
        handleAlreadyProcessed,
      );
    };
  }, [
    paypalBtnRef.current,
    paymentApi.createPaymentIntentPaypal,
    selectedProduct.id,
  ]);

  useEffect(() => {
    if (!paymentApi.createPaymentIntentPaypal) {
      return;
    }

    const handlePaypalIframeEvent = async (
      mes: Record<string, any>,
    ): Promise<void> => {
      const { payment_status, method, error_message, order } = mes.data;

      if (payment_status === 'error') {
        await handlePaymentFail(error_message, method);
      }

      if (payment_status === 'info' || payment_status === 'success') {
        await handlePaymentSuccess(order);
      }
    };

    window.addEventListener('message', handlePaypalIframeEvent);

    return (): void => {
      window.removeEventListener('message', handlePaypalIframeEvent);
    };
  }, [paymentApi.createPaymentIntentPaypal, selectedProduct.id]);

  useEffect(() => {
    if (onPaymentView) {
      onPaymentView({
        productId: selectedProduct.id,
        productName: selectedProduct.name,
      });
    }
  }, []);

  return (
    <div className={styles.container}>
      {isLoading && <Loader />}

      {paymentError ? (
        <PaymentError
          paymentError={paymentError}
          handleCloseError={handleCloseError}
        />
      ) : (
        <div className={styles.slider_outer_wrapper}>
          {!isFormReady && <Loader />}

          {merchantData && (
            <div
              className={clsx(styles.slider_inner_wrapper, {
                [styles.is_shown]: isFormReady,
              })}
            >
              <Swiper
                slidesPerView={1}
                spaceBetween={16}
                initialSlide={0}
                centeredSlides
                onSwiper={(swiper): void => {
                  activeCard === PAYMENT_METHODS.ONE_PAY && swiper.slideNext(); // By invoking this method we trigger slider for IOS Chrome
                }}
                className={clsx(styles.slider, {
                  [styles.is_disabled]: isLoading,
                })}
              >
                <div className={styles.header}>
                  <div className={styles.header__buttons}>
                    {[PAYMENT_METHODS.ONE_PAY, PAYMENT_METHODS.CARD].map(
                      (type) => (
                        <PaymentButton
                          key={type}
                          isActive={activeCard === type}
                          onClick={handlePaymentMethodChange}
                          type={type}
                        />
                      ),
                    )}
                  </div>

                  {SummaryComponent && (
                    <div
                      className={clsx(
                        styles.header__summary,
                        styles['header__summary--bottom'],
                      )}
                    >
                      <SummaryComponent product={selectedProduct} />
                    </div>
                  )}
                </div>

                <SwiperSlide className={styles.slide}>
                  <Payment
                    googlePayContainerRef={googleContainerRef}
                    applePayContainerRef={appleContainerRef}
                    merchantData={merchantData}
                    formParams={formParams || getDefaultFormParams(t)}
                    styles={formStyles || defaultFormStyles}
                    onFail={(e): void =>
                      handlePaymentFail(
                        e.message || '',
                        SOLID_PAYMENT_METHOD_MAPPER[e.entity],
                      )
                    }
                    onSuccess={async (e): Promise<void> =>
                      await handlePaymentSuccess(e.order)
                    }
                    onMounted={(): void => {
                      window.setTimeout(() => {
                        setIsFormReady(true);
                        onFormReady && onFormReady();
                      }, 1000);
                    }}
                    onSubmit={(e): void =>
                      handlePaymentSubmit(SOLID_PAYMENT_METHOD_MAPPER[e.entity])
                    }
                  />
                </SwiperSlide>

                <SwiperSlide className={styles.slide}>
                  <div
                    className={styles['slide__pay_button']}
                    ref={appleContainerRef}
                  />
                  <div
                    className={styles['slide__pay_button']}
                    ref={googleContainerRef}
                  />
                  {paymentApi.createPaymentIntentPaypal && (
                    <div
                      className={styles['slide__pay_button']}
                      id="paypal-button"
                      ref={paypalBtnRef}
                    />
                  )}
                </SwiperSlide>
              </Swiper>
            </div>
          )}
        </div>
      )}

      <div className={styles.guarantee}>
        <Guarantee />
      </div>
    </div>
  );
};

export default PaymentFormV3;
